import React from "react";

import { motion } from "framer-motion";

import { formatCurrency } from "../../../utils/reportHelpers";
import DonutChart from "../charts/DonutChart";
import ChartCard from "../shared/ChartCard";
import MetricTable from "../shared/MetricTable";

const KPI_METRICS = [
  {
    key: "totalAmount",
    label: "Total Invoiced",
    color:
      "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400",
    format: (data) => formatCurrency(data.invoices.totalAmount),
  },
  {
    key: "pendingAmount",
    label: "Pending Amount",
    color:
      "bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400",
    format: (data) => formatCurrency(data.invoices.pendingAmount),
  },
  {
    key: "overdueAmount",
    label: "Overdue Amount",
    color: "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400",
    format: (data) => formatCurrency(data.invoices.overdueAmount),
  },
  {
    key: "collectionRate",
    label: "Collection Rate",
    color: "bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
    format: (data) => `${data.invoices.collectionRate}%`,
  },
];

const AGING_COLUMNS = [
  { key: "range", label: "Age Range", align: "left" },
  { key: "count", label: "Count", align: "center" },
  {
    key: "amount",
    label: "Amount",
    align: "right",
    format: (val) => formatCurrency(val),
  },
  {
    key: "percentage",
    label: "Percentage",
    align: "right",
    format: (val) => `${val}%`,
  },
];

const InvoicesTab = ({ data, loading }) => {
  if (loading || !data) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* KPI skeleton */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              className="rounded-lg p-3 sm:p-4 lg:p-6 bg-gray-200 dark:bg-gray-700 animate-pulse h-20 sm:h-24"
              key={i}
            />
          ))}
        </div>
        {/* Chart skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="h-[360px] rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
          <div className="h-[360px] rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
        </div>
        <div className="h-[300px] rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }

  const statusLabels = Object.keys(data.invoices.statusDistribution).map(
    (s) => s.charAt(0).toUpperCase() + s.slice(1)
  );
  const statusSeries = Object.values(data.invoices.statusDistribution);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* 1. KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {KPI_METRICS.map((metric, index) => (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-lg p-3 sm:p-4 lg:p-6 ${metric.color}`}
            initial={{ opacity: 0, y: 12 }}
            key={metric.key}
            transition={{
              delay: index * 0.08,
              duration: 0.3,
              ease: "easeOut",
            }}
          >
            <p className="text-xs sm:text-sm font-medium opacity-80">
              {metric.label}
            </p>
            <p className="text-lg sm:text-xl lg:text-2xl font-bold mt-1 sm:mt-2">
              {metric.format(data)}
            </p>
          </motion.div>
        ))}
      </div>

      {/* 2. Two-column: Status Distribution + Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 12 }}
          transition={{ delay: 0.35, duration: 0.3, ease: "easeOut" }}
        >
          <ChartCard title="Invoice Status Distribution">
            <DonutChart
              centerLabel="Total"
              height={300}
              labels={statusLabels}
              series={statusSeries}
            />
          </ChartCard>
        </motion.div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 12 }}
          transition={{ delay: 0.45, duration: 0.3, ease: "easeOut" }}
        >
          <ChartCard title="Payment Methods">
            <div className="space-y-4">
              {data.invoices.byPaymentMethod.map((payment, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <i className={`${payment.icon} text-gray-400`} />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {payment.method}
                      </span>
                    </div>
                    <span className="text-gray-600 dark:text-gray-400">
                      {formatCurrency(payment.amount)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700/50 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${payment.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
        </motion.div>
      </div>

      {/* 3. Invoice Aging Report */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        initial={{ opacity: 0, y: 12 }}
        transition={{ delay: 0.55, duration: 0.3, ease: "easeOut" }}
      >
        <ChartCard title="Invoice Aging Report">
          <MetricTable
            striped
            columns={AGING_COLUMNS}
            data={data.invoices.aging}
          />
        </ChartCard>
      </motion.div>
    </div>
  );
};

export default InvoicesTab;
